import { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/enhanced-button";
import { Textarea } from "@/components/ui/textarea";

interface ReplyTarget {
  id: string;
  authorName: string;
  text: string;
}

interface CommentComposeBoxProps {
  replyTarget: ReplyTarget | null;
  onCancelReply: () => void;
  onSubmit: (text: string, parentCommentId?: string) => Promise<void>;
}

export function CommentComposeBox({ replyTarget, onCancelReply, onSubmit }: CommentComposeBoxProps) {
  const [text, setText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!text.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit(text.trim(), replyTarget?.id);
      setText("");
      onCancelReply();
    } catch {
      setError("Failed to post comment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-2">
      {replyTarget && (
        <div className="flex items-start justify-between gap-2 rounded-md bg-muted/40 border px-2.5 py-1.5">
          <div className="min-w-0">
            <p className="text-xs font-medium text-foreground">Replying to {replyTarget.authorName}</p>
            <p className="text-xs text-muted-foreground truncate">{replyTarget.text}</p>
          </div>
          <button onClick={onCancelReply} className="text-muted-foreground hover:text-foreground shrink-0">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      <Textarea
        placeholder={replyTarget ? "Write a reply…" : "Leave a comment…"}
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="min-h-[70px] resize-none text-sm"
        disabled={isSubmitting}
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex justify-end">
        <Button size="sm" onClick={handleSubmit} disabled={!text.trim() || isSubmitting}>
          {isSubmitting ? "Posting…" : replyTarget ? "Reply" : "Comment"}
        </Button>
      </div>
    </div>
  );
}
